/**
 * Vantage · Hunts — the domain model (docs/hunts-requirements.md).
 *
 * A hunt is a STRUCTURED CASE (decision H1): hypothesis, ATT&CK mapping,
 * scope, auto-recorded sessions, pinned evidence, findings, and a closing
 * verdict. Everything here is plain data + pure functions; persistence is
 * lib/hunt-store.ts, UI is the landing page and cockpit.
 *
 * The serialized shape is VERSIONED (`schema`) because today's store is
 * localStorage (decision H8) and tomorrow's is a hunt API — readHunt is the
 * single tolerant door future migrations walk through.
 */

import { readGraph, type HuntGraph } from './hunt-graph'

export type HuntStatus = 'draft' | 'active' | 'closed'
export type Verdict = 'confirmed' | 'refuted' | 'inconclusive' | 'partial'
export type Methodology = 'hypothesis' | 'ioc-sweep' | 'baseline' | 'entity-pivot'
export type Severity = 'low' | 'medium' | 'high' | 'critical'

export const VERDICTS: readonly Verdict[] = ['confirmed', 'refuted', 'inconclusive', 'partial']
export const METHODOLOGIES: readonly Methodology[] = ['hypothesis', 'ioc-sweep', 'baseline', 'entity-pivot']

export const METHODOLOGY_LABEL: Record<Methodology, string> = {
  hypothesis: 'hypothesis-driven',
  'ioc-sweep': 'IOC sweep',
  baseline: 'baseline / anomaly',
  'entity-pivot': 'entity pivoting',
}

/** One recorded query in a session trail — auto-captured on every run (H9). */
export interface TrailStep {
  id: string
  at: number
  query: string
  window?: { start: string; end: string }
  /** Row count the run returned; null when it failed or was cancelled. */
  hitCount: number | null
  durationMs: number | null
  /** Curation: starred steps are the story; pruned steps leave the report. */
  starred: boolean
  pruned: boolean
  note?: string
}

/** One sitting at the cockpit; a hunt accumulates several. */
export interface HuntSession {
  id: string
  startedAt: number
  endedAt: number | null
  title: string
  steps: TrailStep[]
}

export type EvidenceKind = 'event' | 'chart' | 'note'

/**
 * A pinned item on the evidence board. Events keep a RENDERED EXCERPT, never
 * the full payload — the quota rule (S2.2): localStorage holds ~5MB per
 * origin and a raw log line alone can run to kilobytes.
 */
export interface Evidence {
  id: string
  kind: EvidenceKind
  at: number
  title: string
  /** The query that produced it — every item stays reproducible. */
  sourceQuery?: string
  window?: { start: string; end: string }
  /** For events: field -> shortened value. For charts: config summary. */
  excerpt?: Record<string, string>
  note?: string
}

export interface Finding {
  id: string
  at: number
  title: string
  detail: string
  severity: Severity
  evidenceIds: string[]
  /**
   * Set when the finding was escalated to a case. Cases have NO API yet
   * (vantage.cases is a stub) — the escalation is a bundle parked on the
   * hunt until that lands (needs-API ledger item 3).
   */
  escalatedAt?: number
}

export interface AttackRef {
  /** Technique name, e.g. "Kerberoasting". */
  technique: string
  /** T-id when known, e.g. "T1558.003" — attackIdFor resolves best-effort. */
  techniqueId: string | null
}

export interface HuntScope {
  streams: string[]
  /** A DQL duration ("30d") — the window hunting starts from. */
  window: string
}

export interface Hunt {
  schema: 1
  id: string
  name: string
  hypothesis: string
  methodology: Methodology
  attack: AttackRef[]
  scope: HuntScope
  status: HuntStatus
  createdAt: number
  updatedAt: number
  closedAt: number | null
  /** Local-only until the hunt API lands (needs-API ledger). */
  assignee: string | null
  verdict: Verdict | null
  verdictNote: string
  sessions: HuntSession[]
  evidence: Evidence[]
  findings: Finding[]
  /** Set when the hunt was created from a template (E6). */
  templateId?: string
  /** The pivot graph built while hunting (E5) — persists with the case. */
  graph?: HuntGraph
}

/* ------------------------------------------------------------ status machine */

/**
 * draft -> active -> closed, and closed -> active (reopen). Closing REQUIRES
 * a verdict — an unclosed question is what `active` says. A draft cannot
 * close: a hunt that never ran has nothing to conclude; delete it instead.
 */
const TRANSITIONS: Record<HuntStatus, readonly HuntStatus[]> = {
  draft: ['active'],
  active: ['closed'],
  closed: ['active'],
}

export function canTransition(from: HuntStatus, to: HuntStatus): boolean {
  return TRANSITIONS[from].includes(to)
}

/* ------------------------------------------------------------------ factories */

/** Collision-safe id: time-sortable prefix + random tail. */
export function huntId(prefix = 'h'): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10)
  return `${prefix}_${Date.now().toString(36)}_${rand}`
}

export function newHunt(init: {
  name: string
  hypothesis: string
  methodology: Methodology
  attack: AttackRef[]
  scope: HuntScope
  assignee?: string | null
  templateId?: string
}): Hunt {
  const now = Date.now()
  return {
    schema: 1,
    id: huntId(),
    name: init.name,
    hypothesis: init.hypothesis,
    methodology: init.methodology,
    attack: init.attack,
    scope: init.scope,
    status: 'draft',
    createdAt: now,
    updatedAt: now,
    closedAt: null,
    assignee: init.assignee ?? null,
    verdict: null,
    verdictNote: '',
    sessions: [],
    evidence: [],
    findings: [],
    ...(init.templateId ? { templateId: init.templateId } : {}),
  }
}

export function newSession(title: string): HuntSession {
  return { id: huntId('s'), startedAt: Date.now(), endedAt: null, title, steps: [] }
}

export function newTrailStep(init: {
  query: string
  window?: { start: string; end: string }
  hitCount: number | null
  durationMs: number | null
}): TrailStep {
  return {
    id: huntId('t'),
    at: Date.now(),
    query: init.query,
    ...(init.window ? { window: init.window } : {}),
    hitCount: init.hitCount,
    durationMs: init.durationMs,
    starred: false,
    pruned: false,
  }
}

export function newEvidence(init: {
  kind: EvidenceKind
  title: string
  sourceQuery?: string
  window?: { start: string; end: string }
  excerpt?: Record<string, string>
  note?: string
}): Evidence {
  return { id: huntId('e'), at: Date.now(), ...init }
}

export function newFinding(init: {
  title: string
  detail: string
  severity: Severity
  evidenceIds?: string[]
}): Finding {
  return {
    id: huntId('f'),
    at: Date.now(),
    title: init.title,
    detail: init.detail,
    severity: init.severity,
    evidenceIds: init.evidenceIds ?? [],
  }
}

/* -------------------------------------------------------------- serialization */

const str = (v: unknown): string => (typeof v === 'string' ? v : '')
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
const numOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null
const rec = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])

function readWindow(v: unknown): { start: string; end: string } | undefined {
  const o = rec(v)
  if (!o) return undefined
  const start = str(o.start)
  const end = str(o.end)
  return start && end ? { start, end } : undefined
}

function readTrailStep(v: unknown): TrailStep | null {
  const o = rec(v)
  if (!o || !str(o.id) || !str(o.query)) return null
  const win = readWindow(o.window)
  return {
    id: str(o.id),
    at: num(o.at),
    query: str(o.query),
    ...(win ? { window: win } : {}),
    hitCount: numOrNull(o.hitCount),
    durationMs: numOrNull(o.durationMs),
    starred: o.starred === true,
    pruned: o.pruned === true,
    ...(str(o.note) ? { note: str(o.note) } : {}),
  }
}

function readSession(v: unknown): HuntSession | null {
  const o = rec(v)
  if (!o || !str(o.id)) return null
  return {
    id: str(o.id),
    startedAt: num(o.startedAt),
    endedAt: numOrNull(o.endedAt),
    title: str(o.title),
    steps: arr(o.steps).map(readTrailStep).filter((s): s is TrailStep => s !== null),
  }
}

function readEvidence(v: unknown): Evidence | null {
  const o = rec(v)
  const kind = str(o?.kind)
  if (!o || !str(o.id) || (kind !== 'event' && kind !== 'chart' && kind !== 'note')) return null
  const excerptRec = rec(o.excerpt)
  const excerpt = excerptRec
    ? Object.fromEntries(Object.entries(excerptRec).map(([k, val]) => [k, str(val)]))
    : undefined
  const win = readWindow(o.window)
  return {
    id: str(o.id),
    kind,
    at: num(o.at),
    title: str(o.title),
    ...(str(o.sourceQuery) ? { sourceQuery: str(o.sourceQuery) } : {}),
    ...(win ? { window: win } : {}),
    ...(excerpt ? { excerpt } : {}),
    ...(str(o.note) ? { note: str(o.note) } : {}),
  }
}

function readFinding(v: unknown): Finding | null {
  const o = rec(v)
  if (!o || !str(o.id) || !str(o.title)) return null
  const sev = str(o.severity)
  return {
    id: str(o.id),
    at: num(o.at),
    title: str(o.title),
    detail: str(o.detail),
    severity: sev === 'low' || sev === 'medium' || sev === 'high' || sev === 'critical' ? sev : 'medium',
    evidenceIds: arr(o.evidenceIds).map(str).filter(Boolean),
    ...(typeof o.escalatedAt === 'number' && Number.isFinite(o.escalatedAt)
      ? { escalatedAt: o.escalatedAt }
      : {}),
  }
}

/**
 * The tolerant reader: anything that is not a schema-1 hunt with an id and a
 * name comes back null and the store QUARANTINES it rather than crashing the
 * page — corrupt records lose themselves, not the library.
 */
export function readHunt(v: unknown): Hunt | null {
  const o = rec(v)
  if (!o || o.schema !== 1 || !str(o.id) || !str(o.name)) return null
  const status = str(o.status)
  const verdict = str(o.verdict)
  const methodology = str(o.methodology)
  const scope = rec(o.scope)
  return {
    schema: 1,
    id: str(o.id),
    name: str(o.name),
    hypothesis: str(o.hypothesis),
    methodology: (METHODOLOGIES as readonly string[]).includes(methodology)
      ? (methodology as Methodology)
      : 'hypothesis',
    attack: arr(o.attack)
      .map((a) => {
        const ar = rec(a)
        if (!ar || !str(ar.technique)) return null
        return { technique: str(ar.technique), techniqueId: str(ar.techniqueId) || null }
      })
      .filter((a): a is AttackRef => a !== null),
    scope: {
      streams: arr(scope?.streams).map(str).filter(Boolean),
      window: str(scope?.window) || '30d',
    },
    status: status === 'draft' || status === 'active' || status === 'closed' ? status : 'draft',
    createdAt: num(o.createdAt),
    updatedAt: num(o.updatedAt),
    closedAt: numOrNull(o.closedAt),
    assignee: str(o.assignee) || null,
    verdict: (VERDICTS as readonly string[]).includes(verdict) ? (verdict as Verdict) : null,
    verdictNote: str(o.verdictNote),
    sessions: arr(o.sessions).map(readSession).filter((s): s is HuntSession => s !== null),
    evidence: arr(o.evidence).map(readEvidence).filter((e): e is Evidence => e !== null),
    findings: arr(o.findings).map(readFinding).filter((f): f is Finding => f !== null),
    ...(str(o.templateId) ? { templateId: str(o.templateId) } : {}),
    ...(() => {
      const g = readGraph(o.graph)
      return g && g.nodes.length ? { graph: g } : {}
    })(),
  }
}

/* ------------------------------------------------------------------- derived */

/** Unique technique ids (fallback: names) across a set of hunts. */
export function huntedTechniques(hunts: readonly Hunt[]): string[] {
  const out = new Set<string>()
  for (const h of hunts) for (const a of h.attack) out.add(a.techniqueId ?? a.technique)
  return [...out]
}

export interface HuntedTechnique {
  count: number
  verdicts: Verdict[]
}

/**
 * The coverage overlay's read (S4.4): CLOSED hunts only — an open question
 * is not coverage — keyed by BOTH technique id and name so the matrix (which
 * knows cells by name) and ATT&CK ids both resolve. Null-finding hunts count
 * on purpose: a refuted hypothesis is hunted ground.
 */
export function huntedByTechnique(hunts: readonly Hunt[]): Map<string, HuntedTechnique> {
  const out = new Map<string, HuntedTechnique>()
  for (const h of hunts) {
    if (h.status !== 'closed') continue
    for (const a of h.attack) {
      for (const key of [a.techniqueId, a.technique]) {
        if (!key) continue
        const cur = out.get(key) ?? { count: 0, verdicts: [] }
        cur.count += 1
        if (h.verdict) cur.verdicts.push(h.verdict)
        out.set(key, cur)
      }
    }
  }
  return out
}

export interface HuntProgramStats {
  active: number
  closed30d: number
  /** Share of closed hunts that produced at least one finding, 0..1 or null. */
  findingRate: number | null
  techniquesHunted: number
}

export function programStats(hunts: readonly Hunt[], now = Date.now()): HuntProgramStats {
  const closed = hunts.filter((h) => h.status === 'closed')
  const closed30d = closed.filter((h) => (h.closedAt ?? 0) > now - 30 * 24 * 3_600_000)
  return {
    active: hunts.filter((h) => h.status === 'active').length,
    closed30d: closed30d.length,
    findingRate: closed.length
      ? closed.filter((h) => h.findings.length > 0).length / closed.length
      : null,
    techniquesHunted: huntedTechniques(hunts).length,
  }
}
